import { useCallback, useState } from 'react';
import {
  api, type SafeToSpend, type Upcoming, type UpcomingCharge, type CashWallet, type ExpectedRefund,
  type PlannedExpense, type PlannedExpenses, type SavingsTarget, type Subscriptions,
  type SubscriptionEntry,
} from '../api.ts';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import {
  Card, Empty, ErrorState, SalaryQuestion, SectionTitle, Sheet, Skeleton, Field,
} from '../components.tsx';
import { parseAmountInput } from '../format.ts';
import { minorToDecimal } from '@fintrack/core';
import { CATEGORIES } from '@fintrack/core';
import { PullToRefresh } from '../PullToRefresh.tsx';

/**
 * Plan — the forward-looking screen.
 *
 * Every other screen answers "what happened". This one answers "what now":
 * what is left to spend before payday, what is about to be charged, what cash
 * is unaccounted for, and what money is owed back.
 */

function daysLabel(n: number, ar: boolean): string {
  if (n === 0) return ar ? 'اليوم' : 'today';
  if (n === 1) return ar ? 'غداً' : 'tomorrow';
  return ar ? `خلال ${n} أيام` : `in ${n} days`;
}

/**
 * The sentence under the safe-to-spend figure: how long it has to last, and
 * what that comes to per day.
 *
 * The per-day amount is stated here rather than in a box of its own because a
 * lone number cannot say whether it is a limit or a record. In the sentence it
 * is unmistakable — the balance above, divided by the days named right after
 * it.
 *
 * Nothing per-day is claimed when the cycle is overspent: the allowance floors
 * at zero there, and "AED 0.00 a day" is a worse answer than not answering.
 */
function daysLeftLine(
  s: SafeToSpend,
  currency: string,
  privateMoney: (minor: number, currency: string) => string,
  ar: boolean,
  overspent: boolean,
): string {
  const days = s.cycle.remainingDays;
  const untilPayday = s.cycle.anchored ? (ar ? ' حتى الراتب القادم' : ' until payday') : '';

  if (days === 0) return (ar ? 'آخر يوم في الدورة' : 'last day of the cycle') + untilPayday;

  const left = ar
    ? `على مدى ${days} يوم متبقٍ`
    : `over ${days} day${days === 1 ? '' : 's'} left`;
  if (overspent) return left + untilPayday;

  const perDay = privateMoney(s.dailyAllowanceMinor, currency);
  // "a day, over 1 day left" is arithmetic nobody says out loud.
  if (days === 1) {
    return ar
      ? `${perDay} لليوم الأخير${untilPayday}`
      : `${perDay} for the one day left${untilPayday}`;
  }
  // "a day, over 8 days left" invites reading "over" as "more than".
  return ar
    ? `${perDay} يوميًا ${left}${untilPayday}`
    : `${perDay} a day for the ${days} days left${untilPayday}`;
}

export function PlanScreen({ onOpenTransaction }: { onOpenTransaction: (id: string) => void }): JSX.Element {
  const { locale, user, categoryLabel } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';

  const sts = useFetch<SafeToSpend>('/safe-to-spend');
  // A long window, because this is now the only list of recurring charges:
  // a yearly renewal has to appear somewhere, and vanishing for eleven months
  // is how a subscription gets forgotten. The total below counts only what
  // falls before payday, which is the part that has to be covered.
  const upcoming = useFetch<Upcoming>('/upcoming?days=400');
  const cash = useFetch<CashWallet>('/cash');
  const refunds = useFetch<{ refunds: ExpectedRefund[] }>('/refunds/expected');
  const planned = useFetch<PlannedExpenses>('/planned-expenses');
  const savings = useFetch<SavingsTarget>('/savings-target');
  const subs = useFetch<Subscriptions>('/subscriptions');
  const [editing, setEditing] = useState<PlannedExpense | null>(null);
  const [editingCash, setEditingCash] = useState<CashWallet['entries'][number] | null>(null);
  const [asking, setAsking] = useState<SubscriptionEntry | null>(null);
  const [editingCharge, setEditingCharge] = useState<UpcomingCharge | null>(null);

  const [sheet, setSheet] = useState<'cash' | 'assumptions' | 'planned' | 'savings' | 'today' | null>(null);

  const refreshAll = useCallback(
    () => Promise.all([
      sts.reload(), upcoming.reload(), cash.reload(), refunds.reload(),
      planned.reload(), savings.reload(), subs.reload(),
    ]),
    [sts, upcoming, cash, refunds, planned, savings, subs],
  );

  const s = sts.data;

  // Charges categorised as subscriptions that have not been identified yet.
  // They have no date, so they cannot be listed among the dated rows — but
  // they are the only ones that need the user to do something, so they belong
  // in the same place rather than in a section of their own.
  const hasUnconfirmed = (subs.data?.unconfirmed.length ?? 0) > 0;
  const overspent = s ? s.availableMinor < 0 : false;

  return (
    <PullToRefresh onRefresh={refreshAll}>
      <div className="screen">
        <h1 style={{ fontSize: 21, fontWeight: 680, letterSpacing: '-0.02em', marginBottom: 14 }}>
          {ar ? 'الخطة' : 'Plan'}
        </h1>

        {/* ── Safe to spend ───────────────────────────────────────────────── */}
        {sts.loading && !s && <Skeleton height={150} count={1} />}
        {sts.error && <ErrorState message={sts.error} onRetry={sts.reload} />}

        {s && s.basis === 'insufficient_data' && (
          <Card>
            <div className="small strong">{ar ? 'لا توجد بيانات دخل بعد' : 'No income recorded yet'}</div>
            <p className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
              {ar
                ? 'أضف دخلك الشهري في الإعدادات، أو انتظر حتى يصل راتبك — عندها سيظهر هنا ما يمكنك إنفاقه.'
                : 'Add your monthly income in Settings, or wait for a salary to arrive. Then this shows what you can actually spend.'}
            </p>
          </Card>
        )}

        {/* Above the card, because every figure on it follows from this. */}
        {s?.salaryQuestion && (
          <SalaryQuestion question={s.salaryQuestion} currency={currency}
                          onAnswered={() => void refreshAll()} />
        )}

        {s && s.basis !== 'insufficient_data' && (
          <Card>
            <div className="tiny muted" style={{ letterSpacing: '0.06em', fontWeight: 600 }}>
              {ar ? 'متاح للإنفاق' : 'SAFE TO SPEND'}
            </div>
            <div style={{
              fontSize: 34, fontWeight: 700, letterSpacing: '-0.03em', marginTop: 2,
              color: overspent ? 'var(--negative)' : 'var(--text)',
            }}>
              {privateMoney(s.availableMinor, currency)}
            </div>

            {/* The daily figure belongs in this sentence rather than in a
                column of its own.

                It used to sit under the heading "Today", beside a second
                number under "Spent today" — two labels that begin with the
                same word, over two figures that mean opposite things: one is
                a budget, the other is a fact. Nothing on the card said which
                was which, and the pair invited the reading that the app had
                contradicted itself.

                Attached to the days-left phrase, the per-day amount explains
                itself: it is the balance above, divided the way the sentence
                says. */}
            <div className="small muted" style={{ marginTop: 2, lineHeight: 1.55 }}>
              {daysLeftLine(s, currency, privateMoney, ar, overspent)}
            </div>

            {!overspent && (
              <>
                {/* Tappable, because "that is not what I spent" is a reasonable
                    thing to think about a total, and until now there was no way
                    to find out. A figure the app will not show its working for
                    is one the user has to either believe or distrust whole. */}
                <button className="row row--between" style={{
                  marginTop: 14, alignItems: 'baseline', width: '100%', textAlign: 'start',
                }} onClick={() => setSheet('today')}>
                  <span className="tiny muted">
                    {ar ? 'أنفقت اليوم' : 'Spent today'}
                    <span aria-hidden style={{ marginInlineStart: 5 }}>›</span>
                  </span>
                  <span className="strong" style={{
                    fontSize: 18,
                    color: s.spentTodayMinor > s.dailyAllowanceMinor ? 'var(--warning)' : 'var(--text)',
                  }}>
                    {money(s.spentTodayMinor, currency)}
                  </span>
                </button>
                {/* The question the two numbers were being asked to answer
                    between them, answered outright instead. */}
                <div className="tiny" style={{
                  marginTop: 2,
                  textAlign: ar ? 'left' : 'right',
                  color: s.spentTodayMinor > s.dailyAllowanceMinor ? 'var(--warning)' : 'var(--text-secondary)',
                }}>
                  {s.spentTodayMinor > s.dailyAllowanceMinor
                    ? (ar
                        ? `${privateMoney(s.spentTodayMinor - s.dailyAllowanceMinor, currency)} فوق حصة اليوم`
                        : `${privateMoney(s.spentTodayMinor - s.dailyAllowanceMinor, currency)} over today's share`)
                    : (ar
                        ? `${privateMoney(s.dailyAllowanceMinor - s.spentTodayMinor, currency)} متبقٍ لليوم`
                        : `${privateMoney(s.dailyAllowanceMinor - s.spentTodayMinor, currency)} left for today`)}
                </div>
              </>
            )}

            {overspent && (
              <p className="small" style={{ marginTop: 10, lineHeight: 1.6, color: 'var(--negative)' }}>
                {/* Names what was actually set aside. Saying only "upcoming
                    bills" while rent and savings are also subtracted invites
                    the reader to conclude the arithmetic is wrong. */}
                {ar
                  ? 'أنفقت أكثر مما دخل هذه الدورة، بعد تنحية ما هو مخصص أصلًا.'
                  : `You have spent more than came in this cycle, once ${
                      [
                        s.committedMinor > 0 ? 'upcoming bills' : null,
                        s.plannedMinor > 0 ? 'standing costs' : null,
                        s.savingsMinor > 0 ? 'saving' : null,
                      ].filter(Boolean).join(', ') || 'everything already spoken for'
                    } is set aside.`}
              </p>
            )}

            <div className="divider" style={{ marginBlock: 12 }} />
            <div className="tiny muted" style={{ lineHeight: 1.7 }}>
              <div>{ar ? 'الدخل' : 'Income'}: {privateMoney(s.incomeMinor, currency)}</div>
              <div>{ar ? 'المنفق' : 'Spent'}: {money(s.spentMinor, currency)}</div>
              {s.committedMinor > 0 && (
                <div>{ar ? 'فواتير قادمة' : 'Bills to come'}: {money(s.committedMinor, currency)}</div>
              )}
              {s.plannedMinor > 0 && (
                <div>{ar ? 'التزامات ثابتة' : 'Standing costs'}: {money(s.plannedMinor, currency)}</div>
              )}
              {s.savingsMinor > 0 && (
                <div>{ar ? 'ادخار' : 'Saving'}: {privateMoney(s.savingsMinor, currency)}</div>
              )}
            </div>

            {/* A number this consequential should never appear without a way to
                see what it assumed. */}
            <button className="btn btn--ghost btn--sm" style={{ marginTop: 8, padding: 0, minHeight: 0 }}
                    onClick={() => setSheet('assumptions')}>
              {ar ? 'كيف حُسب هذا؟' : 'How is this worked out?'}
            </button>
          </Card>
        )}

        {/* ── The editable plan ───────────────────────────────────────────── */}
        <SectionTitle action={
          <button className="btn btn--ghost btn--sm" onClick={() => setSheet('savings')}>
            {ar ? 'تعديل' : 'Edit'}
          </button>
        }>
          {ar ? 'الادخار' : 'Saving'}
        </SectionTitle>
        <Card>
          {savings.data && (savings.data.amountMinor != null || savings.data.pct != null) ? (
            <>
              <div className="strong" style={{ fontSize: 20 }}>
                {savings.data.pct != null
                  ? `${savings.data.pct}%`
                  : privateMoney(savings.data.amountMinor ?? 0, currency)}
                {s && s.savingsMinor > 0 && savings.data.pct != null && (
                  <span className="small muted" style={{ marginInlineStart: 8 }}>
                    = {privateMoney(s.savingsMinor, currency)}
                  </span>
                )}
              </div>
              <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.6 }}>
                {ar
                  ? 'يُخصم من الراتب قبل حساب المتاح للإنفاق.'
                  : 'Taken off the salary before anything is counted as spendable.'}
              </div>
            </>
          ) : (
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'الادخار ليس ما يتبقى في آخر الشهر. حدّد مبلغًا أو نسبة ليُخصم أولًا.'
                : 'Saving is not what is left at the end of the cycle. Set an amount or a share and it comes off first.'}
            </div>
          )}
        </Card>

        <SectionTitle action={
          <button className="btn btn--ghost btn--sm" onClick={() => setSheet('planned')}>
            {ar ? '+ إضافة' : '+ Add'}
          </button>
        }>
          {ar ? 'التزامات ثابتة' : 'Standing costs'}
        </SectionTitle>
        {planned.data && planned.data.items.length === 0 && (
          <Card>
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'الإيجار والرسوم المدرسية وما يُدفع بشيك لا تصل كرسائل، فلا يعلم بها التطبيق. أضِفها هنا لتُخصم من المتاح.'
                : 'Rent, school fees, anything paid by cheque — none of it arrives as a message, so the app cannot see it. Add it here and it comes off what is available.'}
            </div>
          </Card>
        )}
        {planned.data && planned.data.items.length > 0 && (
          <Card>
            {planned.data.items.map((p, i) => (
              <div key={p.id}>
                {i > 0 && <div className="divider" style={{ marginBlock: 10 }} />}
                <button
                  className="row row--between"
                  style={{ width: '100%', background: 'none', border: 0, padding: 0, textAlign: 'inherit' }}
                  onClick={() => { setEditing(p); setSheet('planned'); }}
                >
                  <div>
                    <div className="strong">{p.name}</div>
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      {p.dueDay ? (ar ? `يوم ${p.dueDay}` : `${p.dueDay}${ordinalSuffix(p.dueDay)} of the month`) : (ar ? 'بدون تاريخ' : 'no set day')}
                      {/* Said plainly, because it is the difference between a
                          figure that is subtracted and one that is not. */}
                      {p.settled && (ar ? ' · خُصم بالفعل' : ' · already out, counted in spending')}
                    </div>
                  </div>
                  <div className="strong" style={{ opacity: p.settled ? 0.45 : 1 }}>
                    {money(p.amountMinor, p.currency)}
                  </div>
                </button>
              </div>
            ))}
            <div className="divider" style={{ marginBlock: 10 }} />
            <div className="row row--between tiny muted">
              <span>{ar ? 'المتبقي خصمه هذه الدورة' : 'Still to come out this cycle'}</span>
              <span className="strong">{money(planned.data.outstandingMinor, currency)}</span>
            </div>
          </Card>
        )}

        {/* ── Upcoming ──────────────────────────────────────────────────────
            One section, not two.

            This screen used to carry "Upcoming" and "Subscriptions" side by
            side, and for most people they listed the same rows with the same
            total — a subscription with a known renewal date is both. What each
            held that the other did not was narrow and invisible: Upcoming also
            covered recurring bills that are not subscriptions, Subscriptions
            also covered renewals beyond the window and charges still waiting to
            be identified.

            So they are merged rather than one deleted, which would have
            silently dropped whichever set the survivor did not hold. The window
            is a year, so nothing falls off the end. */}
        <SectionTitle>{ar ? 'قادم' : 'Upcoming'}</SectionTitle>
        <p className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.6 }}>
          {ar
            ? 'كل ما سيُخصم منك مرة أخرى — الاشتراكات والفواتير المتكررة. الإجمالي أدناه هو ما يقع قبل راتبك القادم.'
            : 'Everything that will charge you again — subscriptions and recurring bills. The total is the part falling before your next payday.'}
        </p>

        {upcoming.data && upcoming.data.charges.length === 0 && !hasUnconfirmed && (
          <Empty icon="◷" title={ar ? 'لا شيء متوقع' : 'Nothing expected'}
                 body={ar
                   ? 'لم يُكتشف أي اشتراك أو فاتورة متكررة بعد.'
                   : 'No recurring bills or subscriptions detected yet.'} />
        )}

        {(upcoming.data?.charges.length || hasUnconfirmed) && (
          <Card className="card--flush">
            {/* Every row is a prediction, and a prediction the user cannot
                correct is one they have to live with being wrong. Tapping opens
                the amount and the date for editing, and offers removal for the
                ones that should never have been detected. */}
            {upcoming.data?.charges.map((c) => (
              <button key={c.id} className="row row--between"
                      style={{ padding: '11px 14px', width: '100%', textAlign: 'start' }}
                      onClick={() => setEditingCharge(c)}>
                <span style={{ minWidth: 0 }}>
                  <span className="strong" style={{ fontSize: 15, display: 'block' }}>
                    {c.merchantDisplay ?? c.merchant}
                  </span>
                  <span className="tiny muted">
                    {daysLabel(c.daysAway, ar)}
                    {c.category ? ` · ${categoryLabel(c.category)}` : ''}
                  </span>
                </span>
                <span style={{ textAlign: ar ? 'left' : 'right', flexShrink: 0 }}>
                  <span className="strong" style={{ display: 'block' }}>
                    {/* A bill that moves around is shown as approximate rather
                        than as a precise figure it cannot promise. */}
                    {c.varies && !c.edited ? '~' : ''}{money(c.amountMinor, c.currency)}
                  </span>
                  <span className="tiny muted">
                    {c.edited
                      ? (ar ? 'عدّلته' : 'edited by you')
                      /* An instalment plan says where it is up to instead of
                         what kind of thing it is. "Subscription" would be
                         wrong on both counts — there is nothing to cancel, and
                         it ends — and how many payments are left is the only
                         thing anybody wants to know about one. */
                      : c.installment
                        ? (ar
                            ? `دفعة ${c.installment.paid} من ${c.installment.total}`
                            : `payment ${c.installment.paid} of ${c.installment.total}`)
                        : c.isSubscription
                          ? (ar ? 'اشتراك' : 'subscription')
                          : (ar ? 'فاتورة' : 'bill')}
                  </span>
                </span>
              </button>
            ))}

            {/* Asked rather than guessed. Detection needs three charges before
                it will commit to an answer; the person who paid needs none —
                they know whether the thing they just bought renews. Answering
                gives the row a date and moves it up into the list above. */}
            {subs.data?.unconfirmed.map((u) => (
              <button key={u.merchant} className="row row--between"
                      style={{ padding: '11px 14px', width: '100%', textAlign: 'start' }}
                      onClick={() => setAsking(u)}>
                <span style={{ minWidth: 0 }}>
                  <span className="strong" style={{ fontSize: 15, display: 'block' }}>{u.merchantDisplay}</span>
                  <span className="tiny" style={{ color: 'var(--accent-text)' }}>
                    {ar ? 'هل يتكرر هذا؟' : 'Does this repeat?'}
                  </span>
                </span>
                <span style={{ textAlign: ar ? 'left' : 'right', flexShrink: 0 }}>
                  <span className="strong" style={{ display: 'block' }}>
                    {money(u.amountMinor, u.currency)}
                  </span>
                  <span className="tiny muted">
                    {new Date(u.lastChargedAt).toLocaleDateString(ar ? 'ar' : 'en-GB',
                      { day: 'numeric', month: 'short' })}
                  </span>
                </span>
              </button>
            ))}

            {upcoming.data && upcoming.data.beforePaydayMinor > 0 && (
              <>
                <div className="divider" />
                <div className="row row--between" style={{ padding: '10px 14px' }}>
                  <span className="small muted">
                    {ar ? 'قبل الراتب القادم' : 'Before your next payday'}
                  </span>
                  <span className="strong">
                    {money(upcoming.data.beforePaydayMinor, currency)}
                  </span>
                </div>
              </>
            )}
          </Card>
        )}

        {/* ── Refunds owed ────────────────────────────────────────────────── */}
        {refunds.data && refunds.data.refunds.length > 0 && (
          <>
            <SectionTitle>{ar ? 'مبالغ مستحقة لك' : 'Owed back to you'}</SectionTitle>
            <Card className="card--flush">
              {refunds.data.refunds.map((r) => (
                <button key={r.id} className="row row--between listrow"
                        style={{ padding: '11px 14px', width: '100%' }}
                        onClick={() => onOpenTransaction(r.transactionId)}>
                  <div style={{ minWidth: 0, textAlign: ar ? 'right' : 'left' }}>
                    {/* A share owed by a person is headed by the person, not
                        the shop: "Khalid" is what you would chase, and the
                        restaurant has nothing to do with it. */}
                    <div className="strong" style={{ fontSize: 15 }}>
                      {r.kind === 'person'
                        ? (r.owedBy || (ar ? 'حصة مشتركة' : 'A shared charge'))
                        : (r.merchant ?? (ar ? 'إرجاع' : 'A return'))}
                    </div>
                    <div className="tiny" style={{ color: r.daysOverdue > 0 ? 'var(--negative)' : 'var(--text-muted)' }}>
                      {r.kind === 'person' && r.merchant ? `${r.merchant} · ` : ''}
                      {r.daysOverdue > 0
                        ? (ar ? `متأخر ${r.daysOverdue} يوم` : `${r.daysOverdue} day${r.daysOverdue === 1 ? '' : 's'} overdue`)
                        : (ar ? `متوقع بحلول ${r.expectedBy}` : `expected by ${r.expectedBy}`)}
                    </div>
                  </div>
                  <div className="strong">{money(r.amountMinor, r.currency)}</div>
                </button>
              ))}
            </Card>
          </>
        )}

        {/* ── Cash ────────────────────────────────────────────────────────── */}
        <SectionTitle>{ar ? 'نقداً' : 'Cash'}</SectionTitle>
        <Card>
          {cash.data && cash.data.withdrawals.length === 0 && cash.data.entries.length === 0 ? (
            <p className="tiny muted" style={{ lineHeight: 1.6 }}>
              {ar
                ? 'لا يمكن لأي تطبيق رؤية ما تنفقه نقداً. عندما تسحب من الصراف، سجّل هنا أين ذهب المبلغ.'
                : 'No app can see what you spend in cash. When you withdraw, record here where it went.'}
            </p>
          ) : (
            <>
              {/* What was actually entered, always. This is the line that was
                  missing: the card led with a figure derived from withdrawals
                  and showed nothing at all about the entries the button below
                  it creates, so recording cash appeared to do nothing. */}
              <div className="row row--between">
                <span className="small muted">{ar ? 'إنفاق نقدي مسجّل' : 'Cash recorded'}</span>
                <span className="strong" style={{ fontSize: 18 }}>
                  {money(cash.data?.spentMinor ?? 0, currency)}
                </span>
              </div>

              {/* Only where there is a withdrawal to be unaccounted for. Shown
                  otherwise it is always zero, which reads as "all accounted
                  for" rather than "there is nothing to account for". */}
              {cash.data?.remainingMinor != null && (
                <div className="row row--between" style={{ marginTop: 6 }}>
                  <span className="small muted">{ar ? 'غير محتسب' : 'Unaccounted for'}</span>
                  <span className="strong" style={{ fontSize: 18 }}>
                    {money(cash.data.remainingMinor, currency)}
                  </span>
                </div>
              )}

              <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
                {cash.data && cash.data.withdrawals.length > 0
                  ? (ar
                      ? 'ما سُجّل مقابل سحب لا يضيف إلى إجماليك — السحب محسوب بالفعل — بل يوضّح أين ذهب. وما لا يرتبط بسحب يُحتسب كمصروف.'
                      : 'Recorded against a withdrawal, it does not add to your totals — the withdrawal already counted — it just says where the money went. Anything not tied to a withdrawal counts as spending.')
                  : (ar
                      ? 'لم يُرصد أي سحب نقدي، لذا يُحتسب ما تسجّله هنا كمصروف عادي ويظهر في إجمالياتك.'
                      : 'No cash withdrawal has been detected, so what you record here counts as ordinary spending and shows up in your totals.')}
              </div>

              {/* Editable, because an amount typed from memory is the entry
                  most likely to be wrong, and one that cannot be corrected is
                  worse than one never recorded — it is a figure the user knows
                  is false and cannot do anything about. */}
              {(cash.data?.entries ?? []).length > 0 && (
                <>
                  <div className="divider" style={{ marginBlock: 12 }} />
                  {(cash.data?.entries ?? []).map((e, i) => (
                    <div key={e.id}>
                      {i > 0 && <div className="divider" style={{ marginBlock: 10 }} />}
                      <button
                        className="row row--between"
                        style={{ width: '100%', background: 'none', border: 0, padding: 0, textAlign: 'inherit' }}
                        onClick={() => { setEditingCash(e); setSheet('cash'); }}
                      >
                        <div>
                          <div className="small strong">
                            {e.merchant || (e.category ? categoryLabel(e.category) : (ar ? 'نقداً' : 'Cash'))}
                          </div>
                          <div className="tiny muted" style={{ marginTop: 2 }}>
                            {new Date(e.occurredAt).toLocaleDateString(ar ? 'ar' : 'en-GB')}
                            {e.withdrawalId && (ar ? ' · من سحب' : ' · from a withdrawal')}
                          </div>
                        </div>
                        <div className="strong">{money(e.amountMinor, e.currency)}</div>
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
          <button className="btn btn--secondary btn--block btn--sm" style={{ marginTop: 12 }}
                  onClick={() => { setEditingCash(null); setSheet('cash'); }}>
            {ar ? 'تسجيل إنفاق نقدي' : 'Record cash spending'}
          </button>
        </Card>

        {sheet === 'assumptions' && s && (
          <Sheet title={ar ? 'كيف حُسب هذا' : 'How this is worked out'} onClose={() => setSheet(null)}>
            <div className="stack">
              {/* Paired by index with the English, so a missing Arabic sentence
                  falls back to the English one rather than to nothing. */}
              {(ar ? s.assumptionsAr ?? s.assumptions : s.assumptions).map((a, i) => (
                <p key={i} className="small" style={{ lineHeight: 1.65 }}>
                  {a || s.assumptions[i]}
                </p>
              ))}
              <div className="divider" />
              <p className="tiny muted" style={{ lineHeight: 1.65 }}>
                {ar
                  ? 'الدورة تبدأ من يوم راتبك وليس من أول الشهر. يمكنك تغيير ذلك في الإعدادات.'
                  : s.cycle.anchored
                    ? 'Your cycle runs from payday to payday rather than from the 1st. You can change this in Settings.'
                    : 'Your cycle follows calendar months, so a salary paid in late July counts towards July rather than the August it pays for. Set your payday in Settings, or wait for a salary to be detected.'}
              </p>
            </div>
          </Sheet>
        )}

        {sheet === 'cash' && (
          <CashSheet
            wallet={cash.data}
            existing={editingCash}
            onClose={() => {
              setSheet(null); setEditingCash(null);
              void cash.reload(); void sts.reload();
            }}
          />
        )}

        {sheet === 'planned' && (
          <PlannedExpenseSheet
            existing={editing}
            onClose={() => {
              setSheet(null); setEditing(null);
              void planned.reload(); void sts.reload();
            }}
          />
        )}

        {sheet === 'savings' && (
          <SavingsSheet
            existing={savings.data}
            onClose={() => { setSheet(null); void savings.reload(); void sts.reload(); }}
          />
        )}

        {editingCharge && (

          <EditChargeSheet

            charge={editingCharge}

            onClose={() => setEditingCharge(null)}

            onChanged={() => { setEditingCharge(null); void upcoming.reload(); void subs.reload(); }}

          />

        )}


        {asking && (
          <SubscriptionAnswerSheet
            entry={asking}
            onClose={() => setAsking(null)}
            onAnswered={() => { setAsking(null); void subs.reload(); void upcoming.reload(); void sts.reload(); }}
          />
        )}

        {sheet === 'today' && s && (
          <TodaySheet
            items={s.todayItems ?? []}
            totalMinor={s.spentTodayMinor}
            onOpenTransaction={onOpenTransaction}
            onClose={() => { setSheet(null); void sts.reload(); }}
          />
        )}
      </div>
    </PullToRefresh>
  );
}

/**
 * "Does this repeat?"
 *
 * The question detection cannot answer from one charge, put to the only party
 * who already knows. Their answer is stronger evidence than three data points
 * would be, so "recurring" writes a real subscription with a real next date
 * rather than a suggestion.
 *
 * The frequency is asked at the same time, in the same tap, because a
 * subscription without one cannot say when it renews — and a renewal date
 * guessed as monthly on a yearly plan would be wrong by eleven months in a list
 * people use to decide what to cancel.
 */
function SubscriptionAnswerSheet({ entry, onClose, onAnswered }: {
  entry: SubscriptionEntry;
  onClose: () => void;
  onAnswered: () => void;
}): JSX.Element {
  const { locale, toastError } = useApp();
  const money = useMoney();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState<string | null>(null);

  const answer = async (a: 'recurring' | 'one_off', frequency?: string) => {
    setBusy(frequency ?? a);
    try {
      await api.post(`/subscriptions/${encodeURIComponent(entry.merchant)}/answer`,
        { answer: a, ...(frequency ? { frequency } : {}) });
      onAnswered();
    } catch (err) {
      toastError(err);
      setBusy(null);
    }
  };

  const every: { key: string; en: string; ar: string }[] = [
    { key: 'monthly', en: 'Every month', ar: 'كل شهر' },
    { key: 'yearly', en: 'Every year', ar: 'كل سنة' },
    { key: 'weekly', en: 'Every week', ar: 'كل أسبوع' },
    { key: 'quarterly', en: 'Every 3 months', ar: 'كل 3 أشهر' },
  ];

  return (
    <Sheet title={entry.merchantDisplay} onClose={onClose}>
      <div className="stack">
        <p className="small muted" style={{ lineHeight: 1.65 }}>
          {ar
            ? `${money(entry.amountMinor, entry.currency)} في ${new Date(entry.lastChargedAt).toLocaleDateString('ar', { day: 'numeric', month: 'long' })}. هل يتكرر هذا الاشتراك؟`
            : `${money(entry.amountMinor, entry.currency)} on ${new Date(entry.lastChargedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}. Does this charge repeat?`}
        </p>

        {every.map((f) => (
          <button key={f.key} className="btn btn--secondary btn--block"
                  disabled={Boolean(busy)} onClick={() => answer('recurring', f.key)}>
            {busy === f.key ? '…' : (ar ? f.ar : f.en)}
          </button>
        ))}

        <div className="divider" />

        <button className="btn btn--ghost btn--block"
                disabled={Boolean(busy)} onClick={() => answer('one_off')}>
          {busy === 'one_off' ? '…' : (ar ? 'مرة واحدة فقط' : 'Just this once')}
        </button>
        <p className="tiny muted" style={{ lineHeight: 1.65 }}>
          {ar
            ? 'المرة الواحدة تبقى في سجل معاملاتك وتختفي من هنا. إذا تكررت لاحقاً فسيلتقطها التطبيق من تلقاء نفسه.'
            : 'A one-off stays in your transactions and leaves this list. If it does start repeating, the app will pick that up on its own.'}
        </p>
      </div>
    </Sheet>
  );
}


function ordinalSuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
}

/**
 * Adding or editing a standing cost.
 *
 * The one question here that is not obvious is whether the item also shows up
 * as a transaction, and it is the one that decides whether the figure is right.
 * Answered wrongly in one direction the amount is subtracted twice and the user
 * is told they have less than they do; in the other it is never subtracted at
 * all and they are told they can spend the rent. So it is asked in plain terms
 * rather than labelled with a word like "tracked".
 */
function PlannedExpenseSheet({ existing, onClose }: {
  existing: PlannedExpense | null;
  onClose: () => void;
}): JSX.Element {
  const { locale, user, toast, categoryLabel } = useApp();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';

  const [name, setName] = useState(existing?.name ?? '');
  const [amount, setAmount] = useState(
    existing ? String(existing.amountMinor / 100) : '',
  );
  const [dueDay, setDueDay] = useState<string>(existing?.dueDay ? String(existing.dueDay) : '');
  const [category, setCategory] = useState(existing?.category ?? '');
  const [appears, setAppears] = useState(existing?.appearsInTransactions ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const amountMinor = parseAmountInput(amount, currency);
      if (!name.trim()) throw new Error(ar ? 'أدخل اسمًا' : 'Give it a name');
      if (!amountMinor || amountMinor <= 0) throw new Error(ar ? 'أدخل مبلغًا' : 'Enter an amount');

      const body = {
        name: name.trim(),
        amountMinor,
        dueDay: dueDay ? Number(dueDay) : null,
        category: category || null,
        appearsInTransactions: appears,
      };
      if (existing) await api.patch(`/planned-expenses/${existing.id}`, body);
      else await api.post('/planned-expenses', body);

      toast(ar ? 'حُفظ' : 'Saved');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    setBusy(true);
    try {
      await api.del(`/planned-expenses/${existing.id}`);
      toast(ar ? 'حُذف' : 'Removed');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={existing
        ? (ar ? 'تعديل التزام' : 'Edit standing cost')
        : (ar ? 'إضافة التزام' : 'Add a standing cost')}
      onClose={onClose}
    >
      <div className="stack">
        <Field label={ar ? 'الاسم' : 'What is it'}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder={ar ? 'إيجار' : 'Rent'} />
        </Field>

        <Field label={`${ar ? 'المبلغ' : 'Amount'} (${currency})`}>
          <input className="input" inputMode="decimal" value={amount}
                 onChange={(e) => setAmount(e.target.value)} placeholder="—" />
        </Field>

        <Field
          label={ar ? 'يوم الخصم (اختياري)' : 'Day of the month (optional)'}
          hint={ar
            ? 'اتركه فارغًا إن لم يكن له يوم ثابت.'
            : 'Leave it blank if there is no fixed day — the amount still counts.'}
        >
          <input className="input" inputMode="numeric" value={dueDay}
                 onChange={(e) => setDueDay(e.target.value.replace(/\D/g, '').slice(0, 2))}
                 placeholder="—" />
        </Field>

        <Field label={ar ? 'التصنيف (اختياري)' : 'Category (optional)'}>
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">—</option>
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{categoryLabel(c.key)}</option>
            ))}
          </select>
        </Field>

        <label className="row" style={{ gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={appears} onChange={(e) => setAppears(e.target.checked)}
                 style={{ marginTop: 3 }} />
          <span>
            <span className="small strong">
              {ar ? 'يظهر أيضًا في رسائل البنك' : 'This also shows up in my bank messages'}
            </span>
            <span className="tiny muted" style={{ display: 'block', marginTop: 3, lineHeight: 1.6 }}>
              {ar
                ? 'إن كان يُدفع بالبطاقة فسيصل كرسالة ويُحتسب مرتين ما لم تحدّد هذا. اتركه فارغًا للإيجار بشيك أو ما يُخصم من حساب آخر.'
                : 'Tick this if you pay it by card, so it is not counted twice once the message arrives. Leave it off for a cheque, or anything paid from another account.'}
            </span>
          </span>
        </label>

        {error && <div className="small" style={{ color: 'var(--negative)' }}>{error}</div>}

        <div className="row" style={{ gap: 10 }}>
          {existing && (
            <button className="btn btn--secondary" disabled={busy} onClick={() => void remove()}>
              {ar ? 'حذف' : 'Remove'}
            </button>
          )}
          <button className="btn btn--primary grow" disabled={busy} onClick={() => void save()}>
            {busy ? '…' : (ar ? 'حفظ' : 'Save')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * What "Spent today" is actually made of.
 *
 * The rows arrive with the total rather than being fetched separately, so this
 * cannot show a list that fails to add up to the heading it opened from — which
 * would turn one doubted number into two.
 */
function TodaySheet({ items, totalMinor, onOpenTransaction, onClose }: {
  items: SafeToSpend['todayItems'];
  totalMinor: number;
  onOpenTransaction: (id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const { locale, user, categoryLabel } = useApp();
  const money = useMoney();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';

  return (
    <Sheet title={ar ? 'أنفقت اليوم' : 'Spent today'} onClose={onClose}>
      <div className="stack">
        <div className="row row--between">
          <span className="small muted">
            {ar
              ? `${items.length} ${items.length === 1 ? 'معاملة' : 'معاملات'}`
              : `${items.length} transaction${items.length === 1 ? '' : 's'} since midnight`}
          </span>
          <span className="strong" style={{ fontSize: 18 }}>
            {money(totalMinor, currency)}
          </span>
        </div>

        {items.length === 0 ? (
          <p className="small muted" style={{ lineHeight: 1.65 }}>
            {ar
              ? 'لا شيء بعد اليوم.'
              : 'Nothing today yet. If you expected something here, it may not have been forwarded to the app.'}
          </p>
        ) : (
          <div>
            {items.map((it) => (
              <button key={it.id} className="row row--between" style={{
                width: '100%', textAlign: 'start', paddingBlock: 9,
                borderTop: '1px solid var(--border)',
              }} onClick={() => { onClose(); onOpenTransaction(it.id); }}>
                <span className="grow">
                  <span className="small strong" style={{ display: 'block' }}>{it.merchant}</span>
                  <span className="tiny muted">
                    {new Date(it.occurredAt).toLocaleTimeString(ar ? 'ar-AE' : 'en-GB',
                      { hour: '2-digit', minute: '2-digit' })}
                    {it.category ? ` · ${categoryLabel(it.category)}` : ''}
                  </span>
                </span>
                <span className="small strong">
                  {money(it.amountMinor, it.currency)}
                </span>
              </button>
            ))}
          </div>
        )}

        <p className="tiny muted" style={{ lineHeight: 1.65 }}>
          {ar
            ? 'يشمل المصروفات والرسوم فقط، منذ منتصف الليل بتوقيتك. التحويلات والمبالغ المستبعدة من التحليلات غير محسوبة.'
            : 'Spending and fees only, since midnight in your timezone. Transfers, refunds and anything you excluded from analytics are not counted.'}
        </p>
      </div>
    </Sheet>
  );
}

/** How much of the salary is spoken for before any of it is spendable. */
function SavingsSheet({ existing, onClose }: {
  existing: SavingsTarget | null;
  onClose: () => void;
}): JSX.Element {
  const { locale, user, toast } = useApp();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';

  // One or the other, never both — the API rejects both, and offering two
  // filled boxes would invite exactly that.
  const [mode, setMode] = useState<'amount' | 'pct'>(existing?.pct != null ? 'pct' : 'amount');
  const [amount, setAmount] = useState(
    existing?.amountMinor != null ? String(existing.amountMinor / 100) : '',
  );
  const [pct, setPct] = useState(existing?.pct != null ? String(existing.pct) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (clear = false) => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/savings-target', clear
        ? { amountMinor: null, pct: null }
        : mode === 'pct'
          ? { pct: Number(pct) || 0, amountMinor: null }
          : { amountMinor: parseAmountInput(amount, currency) ?? 0, pct: null });
      toast(ar ? 'حُفظ' : 'Saved');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={ar ? 'الادخار' : 'Saving'} onClose={onClose}>
      <div className="stack">
        <p className="tiny muted" style={{ lineHeight: 1.65 }}>
          {ar
            ? 'يُخصم هذا من الراتب قبل حساب ما يمكن إنفاقه، لأن الادخار ليس ما يتبقى في آخر الشهر.'
            : 'This comes off the salary before anything is counted as spendable, because saving is not what happens to be left at the end.'}
        </p>

        <div className="row" style={{ gap: 8 }}>
          <button className={`chip${mode === 'amount' ? ' chip--active' : ''}`}
                  onClick={() => setMode('amount')}>
            {ar ? 'مبلغ ثابت' : 'A fixed amount'}
          </button>
          <button className={`chip${mode === 'pct' ? ' chip--active' : ''}`}
                  onClick={() => setMode('pct')}>
            {ar ? 'نسبة من الدخل' : 'A share of income'}
          </button>
        </div>

        {mode === 'amount' ? (
          <Field label={`${ar ? 'المبلغ لكل دورة' : 'Per cycle'} (${currency})`}>
            <input className="input" inputMode="decimal" value={amount}
                   onChange={(e) => setAmount(e.target.value)} placeholder="—" />
          </Field>
        ) : (
          <Field
            label={ar ? 'النسبة %' : 'Percent of income'}
            hint={ar
              ? 'تُحسب من الدخل الذي وصل فعلًا هذه الدورة.'
              : 'Taken from the income that actually arrived this cycle, so a short month saves less.'}
          >
            <input className="input" inputMode="decimal" value={pct}
                   onChange={(e) => setPct(e.target.value.replace(/[^\d.]/g, '').slice(0, 5))}
                   placeholder="20" />
          </Field>
        )}

        {error && <div className="small" style={{ color: 'var(--negative)' }}>{error}</div>}

        <div className="row" style={{ gap: 10 }}>
          <button className="btn btn--secondary" disabled={busy} onClick={() => void save(true)}>
            {ar ? 'إيقاف' : 'Turn off'}
          </button>
          <button className="btn btn--primary grow" disabled={busy} onClick={() => void save()}>
            {busy ? '…' : (ar ? 'حفظ' : 'Save')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** Recording, correcting, or removing cash spending. */
function CashSheet({ wallet, existing, onClose }: {
  wallet: CashWallet | null;
  existing: CashWallet['entries'][number] | null;
  onClose: () => void;
}): JSX.Element {
  const { locale, user, toast, toastError } = useApp();
  const money = useMoney();
  const ar = locale === 'ar';
  const currency = user?.currency ?? 'AED';

  const [amount, setAmount] = useState(existing ? String(existing.amountMinor / 100) : '');
  const [category, setCategory] = useState(existing?.category ?? 'groceries');
  const [withdrawalId, setWithdrawalId] = useState<string>(existing?.withdrawalId ?? '');
  const [merchant, setMerchant] = useState(existing?.merchant ?? '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const minor = parseAmountInput(amount, currency);
    if (!minor || minor <= 0) {
      toast(ar ? 'أدخل مبلغاً صحيحاً' : 'Enter a valid amount', 'error');
      return;
    }
    setBusy(true);
    try {
      const body = {
        amountMinor: minor,
        category,
        merchant: merchant || null,
        withdrawalId: withdrawalId || null,
      };
      if (existing) await api.patch(`/cash/${existing.id}`, body);
      else await api.post('/cash', body);
      toast(existing ? (ar ? 'حُفظ' : 'Saved') : (ar ? 'تم التسجيل' : 'Recorded'));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    setBusy(true);
    try {
      await api.del(`/cash/${existing.id}`);
      toast(ar ? 'حُذف' : 'Removed');
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // The withdrawal this entry is already attached to stays selectable even with
  // nothing left on it, or editing an entry would silently detach it — turning
  // a reclassification into new spending and adding the amount to the totals.
  const open = (wallet?.withdrawals ?? [])
    .filter((w) => w.remainingMinor > 0 || w.id === existing?.withdrawalId);

  return (
    <Sheet
      title={existing
        ? (ar ? 'تعديل إنفاق نقدي' : 'Edit cash spending')
        : (ar ? 'إنفاق نقدي' : 'Cash spending')}
      onClose={onClose}
    >
      <div className="stack">
        <Field label={ar ? 'المبلغ' : 'Amount'}>
          <input className="select" inputMode="decimal" value={amount} placeholder="0.00"
                 onChange={(e) => setAmount(e.target.value)} />
        </Field>

        <Field label={ar ? 'الفئة' : 'Category'}>
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{ar ? c.ar : c.en}</option>
            ))}
          </select>
        </Field>

        <Field label={ar ? 'التاجر (اختياري)' : 'Merchant (optional)'}>
          <input className="select" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
        </Field>

        {open.length > 0 && (
          <Field label={ar ? 'من أي سحب؟' : 'From which withdrawal?'}>
            <select className="select" value={withdrawalId} onChange={(e) => setWithdrawalId(e.target.value)}>
              <option value="">{ar ? 'غير محدد' : 'Not sure'}</option>
              {open.map((w) => (
                <option key={w.id} value={w.id}>
                  {new Date(w.occurredAt).toLocaleDateString(ar ? 'ar' : 'en-GB')}
                  {' · '}
                  {money(w.remainingMinor, w.currency)} {ar ? 'متبقٍ' : 'left'}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* Which of these is true depends entirely on the field above, so the
            sentence follows it. Stating only the withdrawal case — as this did
            — told users their entry would not affect their totals at the exact
            moment it was about to. */}
        <p className="tiny muted" style={{ lineHeight: 1.6 }}>
          {withdrawalId
            ? (ar
                ? 'هذا لا يزيد إجمالي إنفاقك. السحب من الصراف محسوب بالفعل — هذا يوضّح فقط على ماذا أُنفق.'
                : 'This does not increase your spending total. The withdrawal already counted — this only says what it was spent on.')
            : (ar
                ? 'لا يرتبط هذا بأي سحب، لذا يُحتسب كمصروف عادي ويظهر في إجمالياتك.'
                : 'This is not tied to a withdrawal, so it counts as ordinary spending and will show up in your totals.')}
        </p>

        <div className="row" style={{ gap: 10 }}>
          {existing && (
            <button className="btn btn--secondary" disabled={busy} onClick={() => void remove()}>
              {ar ? 'حذف' : 'Remove'}
            </button>
          )}
          <button className="btn btn--primary grow" disabled={busy} onClick={submit}>
            {busy ? '…' : (ar ? 'حفظ' : 'Save')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Correcting a predicted charge.
 *
 * Detection is a guess, and the two things it gets wrong are the amount and
 * the date — a bill that changed price, a renewal that moved. Both are
 * editable here, and a charge that should never have been detected at all can
 * be removed outright.
 *
 * Clearing a field hands it back to detection rather than storing a blank, so
 * a correction is never a one-way door: the row goes back to tracking whatever
 * the transactions say.
 */
function EditChargeSheet({ charge, onClose, onChanged }: {
  charge: UpcomingCharge;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [amount, setAmount] = useState(
    String(minorToDecimal(charge.amountMinor, charge.currency)),
  );
  const [dueOn, setDueOn] = useState(charge.dueOn.slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const trimmed = amount.trim();
    const amountMinor = trimmed ? parseAmountInput(trimmed, charge.currency) : null;
    if (trimmed && amountMinor == null) {
      setError(ar ? 'مبلغ غير صالح' : 'That amount could not be read');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/recurring/${charge.id}`, {
        amountMinor,
        // Midday, so the stored instant lands on the day the user picked
        // whatever timezone reads it back.
        nextDate: dueOn ? new Date(`${dueOn}T12:00:00`).toISOString() : null,
      });
      toast(ar ? 'حُفظ' : 'Saved');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const name = charge.merchantDisplay ?? charge.merchant;
    if (!window.confirm(ar
      ? `إزالة ${name} من القادم؟ لن يُكتشف مرة أخرى.`
      : `Remove ${name} from Upcoming? It will not be detected again.`)) return;
    setBusy(true);
    try {
      await api.del(`/recurring/${charge.id}`);
      toast(ar ? 'أُزيل' : 'Removed');
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={charge.merchantDisplay ?? charge.merchant} onClose={onClose}>
      <div className="stack">
        <Field label={`${ar ? 'المبلغ' : 'Amount'} (${charge.currency})`} error={error}>
          <input className="input" inputMode="decimal" value={amount}
                 onChange={(e) => setAmount(e.target.value)} />
        </Field>

        <Field label={ar ? 'التاريخ القادم' : 'Next charge'}>
          <input className="input" type="date" value={dueOn}
                 onChange={(e) => setDueOn(e.target.value)} />
        </Field>

        <button className="btn btn--primary btn--block" disabled={busy} onClick={() => void save()}>
          {busy ? '…' : (ar ? 'حفظ' : 'Save')}
        </button>

        {/* The escape hatch for a detection that is simply wrong. Kept visually
            quiet and behind a confirmation: it is reversible only by the charge
            being detected afresh, which for something wrongly detected is
            never. */}
        <button className="btn btn--ghost btn--block btn--sm"
                style={{ color: 'var(--negative)' }}
                disabled={busy} onClick={() => void remove()}>
          {ar ? 'إزالة من القادم' : 'Remove from Upcoming'}
        </button>

        <p className="tiny muted" style={{ lineHeight: 1.6 }}>
          {ar
            ? 'المبلغ والتاريخ مُقدَّران من حركاتك. ما تحفظه هنا يُستخدم بدلًا منهما حتى تمسحه.'
            : 'The amount and date are worked out from your transactions. What you save here is used instead, until you clear it.'}
        </p>
      </div>
    </Sheet>
  );
}
